import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import LockOverlay from "./components/LockOverlay";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobForm = lazy(() => import("./pages/JobForm"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Users = lazy(() => import("./pages/Users"));
const Customers = lazy(() => import("./pages/Customers"));
const PublicJob = lazy(() => import("./pages/PublicJob"));
const Settings = lazy(() => import("./pages/Settings"));
const ShareTarget = lazy(() => import("./pages/ShareTarget"));
const Payments = lazy(() => import("./pages/Payments"));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-[#737373] text-sm">Loading…</div>
);

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#737373] text-sm">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
};

const LoginGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#737373] text-sm">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/share/:token" element={<PublicJob />} />
        <Route path="/" element={<LoginGate />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/jobs" element={<Protected roles={["admin", "sales"]}><Jobs /></Protected>} />
        <Route path="/customers" element={<Protected roles={["admin", "sales"]}><Customers /></Protected>} />
        <Route path="/jobs/new" element={<Protected roles={["admin", "sales"]}><JobForm /></Protected>} />
        <Route path="/jobs/:id/edit" element={<Protected roles={["admin", "sales"]}><JobForm /></Protected>} />
        <Route path="/jobs/:id" element={<Protected><JobDetail /></Protected>} />
        <Route path="/calendar" element={<Protected roles={["admin", "sales", "team"]}><Calendar /></Protected>} />
        <Route path="/payments" element={<Protected roles={["admin", "sales"]}><Payments /></Protected>} />
        <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
        <Route path="/settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
        <Route path="/share-target" element={<Protected><ShareTarget /></Protected>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  useEffect(() => {
    const t = setTimeout(() => { try { sessionStorage.removeItem("tm_chunk_reloaded"); } catch (e) {} }, 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <LockOverlay>
              <AppRouter />
            </LockOverlay>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ErrorBoundary>
      </AuthProvider>
    </div>
  );
}

export default App;
